'use client';

import { useQuery } from '@tanstack/react-query';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

import { LandingSection } from '@ui/sections/landing/LandingSection';
import { HomeSearchBar } from '@website/components/HomeSearchBar';
import { isFeatureEnabled } from '@website/config/featureFlags';
import { useCurrentPosition } from '@website/hooks/useCurrentPosition';
import { useCurrentUser, useUserType } from '@website/hooks/useCurrentUser';

import { AuthenticatedLandingSection } from './AuthenticatedLandingSection';

/**
 * Landing section wrapper that shows different content based on user type.
 *
 * Two-pass rendering strategy for SEO without hydration warnings:
 * 1. Server & first client render: Always renders public landing section (SEO-friendly)
 * 2. After mount: Updates to show the correct version based on user cookie
 *
 * This ensures:
 * - SEO: Public content is in the initial HTML
 * - No hydration warnings: Server and client render the same content initially
 * - Correct UX: Authenticated users see their version after a single render cycle
 */
export const LandingSectionWrapper = () => {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const userType = useUserType(); // From cookie (with initialData)
  const { data: user } = useCurrentUser(); // Full user data for redirect logic

  // Two-pass rendering: render public version first (matches server), then update
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Server and first client render: always 'public' to match and avoid hydration warning
  // After mount: use actual userType from cookie
  const effectiveUserType = isClient ? userType : 'public';

  // Redirect professionals to dashboard (fallback if middleware didn't catch it)
  useEffect(() => {
    if (user?.isProfessional) {
      router.replace(`/${locale}/dashboard`);
    }
  }, [user?.isProfessional, locale, router]);

  // Get position from the hook - this updates instantly when user grants permission
  const { data: position, isLoading: isPositionLoading, hasBrowserLocation } = useCurrentPosition();

  // Use full precision coordinates to match what activities use (no rounding)
  // This ensures the city name and activities are from the exact same location
  const lat = position?.latitude;
  const lng = position?.longitude;

  // Reverse geocoding query - uses position from useCurrentPosition
  // The query key includes hasBrowserLocation to ensure refetch when switching from IP to browser
  const {
    data: reverseGeocodedLocation,
    isLoading: isReverseGeocodingLoading,
    isFetched: isReverseGeocodingFetched
  } = useQuery({
    queryKey: ['geolocation', 'reverseGeocode', lat, lng, hasBrowserLocation],
    queryFn: async () => {
      if (lat === undefined || lng === undefined) return null;

      console.log('[LOCATION] Reverse geocoding for:', { lat, lng, hasBrowserLocation });
      const response = await fetch(`/api/location/reverse?lat=${lat}&lon=${lng}`);
      if (!response.ok) return null;

      const data = await response.json();
      console.log('[LOCATION] Reverse geocoding result:', data);
      return {
        name: data.name,
        city: data.city,
        description: data.description
      };
    },
    // Fetch only for authenticated users with browser geolocation (not IP - too inaccurate)
    enabled: !!user && !!position && hasBrowserLocation,
    refetchOnWindowFocus: false,
    staleTime: Infinity, // Cache based on query key - different hasBrowserLocation = different cache
    retry: 1
  });

  // Extract city name - ONLY from reverse geocoded browser location
  // Do NOT use IP location for city name (too inaccurate)
  let cityName: string | undefined;
  let showGenericMessage = false;

  // Show generic message while position is loading or reverse geocoding is in progress
  const isStillLoading = isPositionLoading || (!!position && hasBrowserLocation && isReverseGeocodingLoading);

  if (isStillLoading) {
    showGenericMessage = true;
  } else if (reverseGeocodedLocation) {
    // Use reverse geocoded city (from browser location only)
    if (reverseGeocodedLocation.city) {
      cityName = reverseGeocodedLocation.city;
    } else if (reverseGeocodedLocation.name && reverseGeocodedLocation.name !== 'Unknown Location') {
      const nameParts = reverseGeocodedLocation.name.split(',');
      cityName = nameParts[0]?.trim();
    }
  }

  // No IP city fallback - IP location is too inaccurate for displaying city name
  // If we only have IP location, show generic message instead

  // If still no city and all loading is done, show generic message
  if (!cityName && !isStillLoading && (isReverseGeocodingFetched || !hasBrowserLocation)) {
    showGenericMessage = true;
  }

  const handleSearch = useCallback(
    (query: string, location?: { latitude: number; longitude: number; name: string }) => {
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      if (location) {
        params.set('displayName', location.name);
        params.set('lat', String(location.latitude));
        params.set('lng', String(location.longitude));
      }
      router.push(`/${locale}/search?${params.toString()}`);
    },
    [locale, router]
  );

  // Use effectiveUserType for rendering decisions (two-pass rendering)
  // Professional users: don't render - redirect is in progress
  if (effectiveUserType === 'professional') {
    return null;
  }

  // Determine which content to render based on user type
  if (effectiveUserType === 'learner') {
    // Learner users: show authenticated landing section
    if (showGenericMessage || !cityName) {
      return <AuthenticatedLandingSection showGenericMessage={true} onSearch={handleSearch} />;
    }
    return <AuthenticatedLandingSection currentLocation={cityName} onSearch={handleSearch} />;
  }

  // Public users: show default landing section
  const hasValidLocation = !!position && !!cityName;
  const title = hasValidLocation ? t('HomePage.landing.titleWithLocation') : t('HomePage.landing.title');
  const highlightedText = hasValidLocation ? cityName : t('HomePage.landing.highlight');

  return (
    <LandingSection
      title={title}
      highlightedText={highlightedText}
      subtitle={t('HomePage.landing.subtitle')}
      searchBar={isFeatureEnabled('KEYWORD_SEARCH') ? <HomeSearchBar onSearch={handleSearch} /> : undefined}
      musicDancesLabel={t('HomePage.landing.musicDancesLabel')}
      personalDevelopmentLabel={t('HomePage.landing.personalDevelopmentLabel')}
      healthWellnessLabel={t('HomePage.landing.healthWellnessLabel')}
    />
  );
};
